/**
 * 1:1 썸네일 크롭 다이얼로그 — 레거시 antd-img-crop(aspect 1, zoomSlider) 대체.
 */
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import {
  popupPrimaryButtonClassName,
  popupSecondaryButtonClassName,
} from '@/shared/components/ui/popup-styles';
import { getCroppedImageFile } from '@/shared/utils/cropImage';
import React, { useCallback, useEffect, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';

export interface ImageCropDialogProps {
  open: boolean;
  imageSrc: string | null;
  fileName?: string;
  /** 기본 1 (레거시 ImgCrop aspect={1/1}) */
  aspect?: number;
  onOpenChange: (open: boolean) => void;
  onConfirm: (file: File) => void | Promise<void>;
}

export const ImageCropDialog: React.FC<ImageCropDialogProps> = ({
  open,
  imageSrc,
  fileName = 'thumbnail.jpg',
  aspect = 1,
  onOpenChange,
  onConfirm,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  }, [open, imageSrc]);

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setIsConfirming(true);
    try {
      const mimeType = fileName.toLowerCase().endsWith('.png')
        ? 'image/png'
        : 'image/jpeg';
      const file = await getCroppedImageFile(
        imageSrc,
        croppedAreaPixels,
        fileName,
        mimeType,
        1,
      );
      await onConfirm(file);
      onOpenChange(false);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg gap-4">
        <DialogHeader>
          <DialogTitle>썸네일 자르기</DialogTitle>
          <DialogDescription>
            1:1 비율로 표시할 영역을 선택하세요. 확대·축소 후 확인을 눌러
            주세요.
          </DialogDescription>
        </DialogHeader>

        <div className="relative h-[320px] w-full overflow-hidden rounded-12 bg-ruda-grey-900">
          {imageSrc ? (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          ) : null}
        </div>

        <div className="space-y-2">
          <label
            htmlFor="thumbnail-crop-zoom"
            className="text-sm tracking-tight text-ruda-text-secondary"
          >
            확대/축소
          </label>
          <input
            id="thumbnail-crop-zoom"
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-full accent-ruda-primary"
          />
        </div>

        <DialogFooter>
          <Button
            type="button"
            className={popupSecondaryButtonClassName}
            onClick={() => onOpenChange(false)}
            disabled={isConfirming}
          >
            취소
          </Button>
          <Button
            type="button"
            className={popupPrimaryButtonClassName}
            onClick={() => void handleConfirm()}
            disabled={!croppedAreaPixels || isConfirming}
          >
            {isConfirming ? '처리 중…' : '확인'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
